import { useState } from "react";
import { Navigate, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import { useAuth } from "@/auth";
import { ApiError, API_BASE_URL } from "@/lib/api";
import { signUp, signIn } from "@/lib/auth-api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";

type Tab = "signup" | "signin";
type FormState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "verification_sent"; email: string };

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { state, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState<Tab>("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formState, setFormState] = useState<FormState>({ kind: "idle" });

  const locState = location.state as { returnTo?: string } | null;
  const returnTo = locState?.returnTo ?? "/dashboard";

  const orgUrl = (path: string) => {
    const base = "https://roadbridge.org";
    const q = i18n.language !== "en" ? `?lang=${encodeURIComponent(i18n.language)}` : "";
    return `${base}${path}${q}`;
  };

  if (state.kind === "authenticated") {
    return <Navigate to={returnTo} replace />;
  }

  const justDeleted = searchParams.get("deleted") === "1";

  const oauthHref = (provider: "google" | "apple") =>
    `${API_BASE_URL}/auth/login?provider=${provider}&redirect=${encodeURIComponent(returnTo)}`;

  const stats = [
    { val: t("landing.stat94"), label: t("landing.stat94Label") },
    { val: t("landing.stat50"), label: t("landing.stat50Label") },
    { val: t("landing.stat6"), label: t("landing.stat6Label") },
    { val: t("landing.stat80k"), label: t("landing.stat80kLabel") },
  ];

  const features = [
    t("landing.feature1"),
    t("landing.feature2"),
    t("landing.feature3"),
    t("landing.feature4"),
    t("landing.feature5"),
  ];

  const badges = [
    t("landing.badgeNoCC"),
    t("landing.badge6Lang"),
    t("landing.badge50States"),
    t("landing.badgeOffline"),
  ];

  function switchTab(next: Tab) {
    setTab(next);
    setFormState({ kind: "idle" });
    setPassword("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormState({ kind: "loading" });
    try {
      if (tab === "signup") {
        const result = await signUp(email, password);
        if (result.status === "verification_sent") {
          setFormState({ kind: "verification_sent", email: result.email ?? email });
        } else {
          await refresh();
          navigate(returnTo, { replace: true });
        }
      } else {
        await signIn(email, password);
        await refresh();
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setFormState({ kind: "error", message: t("landing.errorEmailNotVerified") });
        } else if (err.status === 401) {
          setFormState({ kind: "error", message: t("landing.errorInvalidCredentials") });
        } else {
          const msg = err.message || t("landing.errorGeneric");
          setFormState({ kind: "error", message: msg });
        }
      } else {
        setFormState({ kind: "error", message: t("landing.errorGeneric") });
      }
    }
  }

  const isLoading = formState.kind === "loading";

  return (
    <div className="flex min-h-screen flex-col md:flex-row">

      {/* ── Left: brand hero panel ── */}
      <div className="flex flex-col bg-indigo-950 px-8 py-10 text-white md:w-1/2 lg:px-12">

        {/* Logo */}
        <div>
          <Logo onDark />
          <p className="mt-1 text-sm text-white/75">{t("landing.tagline")}</p>
        </div>

        {/* Hero copy */}
        <div className="mt-10 flex-1">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight lg:text-4xl">
            {t("landing.heroHeadline")}{" "}
            <span className="text-brand-accent">{t("landing.heroAccent")}</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-white/80">
            {t("landing.heroBody")}
          </p>

          {/* Stats grid */}
          <div className="mt-8 grid grid-cols-2 gap-3">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-white/10 p-4">
                <div className="text-2xl font-bold text-brand-accent">{s.val}</div>
                <div className="mt-0.5 text-xs text-white/80">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Feature checklist */}
          <ul className="mt-8 space-y-2" aria-label="Key features">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                <svg
                  className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8.5 6.5 12 13 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Testimonial */}
        <div className="mt-10 rounded-lg bg-white/10 p-4">
          <p className="text-sm text-brand-accent" aria-label={t("landing.testimonialStars")}>
            ★★★★★
          </p>
          <p className="mt-2 text-sm italic text-white/85">
            {t("landing.testimonial1Text")}
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-accent text-xs font-bold text-indigo-950"
              aria-hidden="true"
            >
              MG
            </div>
            <div>
              <p className="text-xs font-semibold text-white/90">{t("landing.testimonial1Author")}</p>
              <p className="text-xs text-white/80">{t("landing.testimonial1Origin")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: auth panel ── */}
      <div className="flex flex-1 flex-col bg-white">
        <div className="flex justify-end px-8 pt-4">
          <LanguageSwitcher
            currentLang={i18n.language}
            onChange={(l) => void i18n.changeLanguage(l)}
            variant="compact"
          />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-8 pb-12">
          {justDeleted && (
            <div
              role="status"
              aria-live="polite"
              data-testid="account-deleted-toast"
              className="mb-6 w-full max-w-sm rounded-md border border-success/40 bg-success/5 px-4 py-3 text-sm text-success"
            >
              {t("settings.dangerZone.successToast")}
            </div>
          )}

          {state.kind === "loading" ? (
            <div
              role="status"
              aria-live="polite"
              data-testid="login-loading"
              className="py-4 text-center text-sm text-neutral-600"
            >
              {t("auth.loading")}
            </div>
          ) : (
            <div className="w-full max-w-sm">

              {/* Tabs */}
              <div className="flex border-b border-neutral-200" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "signup"}
                  onClick={() => switchTab("signup")}
                  className={`flex-1 pb-3 text-sm font-semibold transition-colors ${
                    tab === "signup"
                      ? "border-b-2 border-brand-primary text-brand-primary"
                      : "text-neutral-600 hover:text-neutral-800"
                  }`}
                >
                  {t("landing.tabCreate")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "signin"}
                  onClick={() => switchTab("signin")}
                  className={`flex-1 pb-3 text-sm font-semibold transition-colors ${
                    tab === "signin"
                      ? "border-b-2 border-brand-primary text-brand-primary"
                      : "text-neutral-600 hover:text-neutral-800"
                  }`}
                >
                  {t("landing.tabSignIn")}
                </button>
              </div>

              {formState.kind === "verification_sent" ? (
                /* Verification-sent state — shown after successful signup */
                <div
                  role="status"
                  aria-live="polite"
                  data-testid="verification-sent"
                  className="mt-8"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/10">
                    <svg className="h-6 w-6 text-brand-primary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2 className="mt-4 font-display text-xl font-bold text-brand-primary">
                    {t("landing.verificationSentTitle")}
                  </h2>
                  <p className="mt-2 text-sm text-neutral-600">
                    {t("landing.verificationSentBody", { email: formState.email })}
                  </p>
                </div>
              ) : (
                <>
                  {/* Heading */}
                  <div className="mt-8">
                    <h2 className="font-display text-2xl font-bold text-brand-primary">
                      {tab === "signup" ? t("landing.createHeading") : t("landing.signInHeading")}
                    </h2>
                    <p className="mt-1.5 text-sm text-neutral-600">
                      {tab === "signup" ? t("landing.createSubheading") : t("landing.signInSubheading")}
                    </p>
                  </div>

                  {/* OAuth buttons */}
                  <div className="mt-6 space-y-3">
                    <a
                      href={oauthHref("google")}
                      data-testid="login-google-button"
                      className="flex w-full items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-brand-primary shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    >
                      <GoogleIcon />
                      {t("landing.continueGoogle")}
                    </a>
                    <a
                      href={oauthHref("apple")}
                      data-testid="login-apple-button"
                      className="flex w-full items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-brand-primary shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    >
                      <AppleIcon />
                      {t("landing.continueApple")}
                    </a>
                  </div>

                  {/* Divider */}
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-neutral-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-neutral-500">{t("landing.orContinueWith")}</span>
                    </div>
                  </div>

                  {/* Error banner */}
                  {formState.kind === "error" && (
                    <div
                      role="alert"
                      className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                      {formState.message}
                    </div>
                  )}

                  {/* Email/password form */}
                  <form onSubmit={(e) => void handleSubmit(e)} noValidate>
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="auth-email"
                          className="mb-1 block text-xs font-medium text-neutral-700"
                        >
                          {t("landing.emailLabel")}
                        </label>
                        <input
                          id="auth-email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isLoading}
                          className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-brand-primary placeholder-neutral-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-60"
                        />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <label
                            htmlFor="auth-password"
                            className="block text-xs font-medium text-neutral-700"
                          >
                            {t("landing.passwordLabel")}
                          </label>
                          {tab === "signin" && (
                            <a
                              href="/forgot-password"
                              className="text-xs text-brand-primary underline hover:text-brand-primary/80"
                            >
                              {t("landing.forgotPassword")}
                            </a>
                          )}
                        </div>
                        <input
                          id="auth-password"
                          type="password"
                          autoComplete={tab === "signup" ? "new-password" : "current-password"}
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={isLoading}
                          className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-brand-primary placeholder-neutral-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-60"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !email || !password}
                      data-testid="login-button"
                      className="mt-5 w-full rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:opacity-50"
                    >
                      {isLoading
                        ? t("landing.submitting")
                        : tab === "signup"
                          ? t("landing.signUpButton")
                          : t("landing.signInButton")}
                    </button>
                  </form>
                </>
              )}

              {/* Badges */}
              <p className="mt-6 text-center text-xs font-medium text-neutral-600">
                {t("landing.freeToStart")}
              </p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {badges.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-xs text-neutral-600"
                  >
                    {badge}
                  </span>
                ))}
              </div>

              {/* Terms */}
              <p className="mt-6 text-center text-xs text-neutral-600">
                <Trans
                  i18nKey="landing.termsAgreement"
                  components={{
                    terms: (
                      <a
                        href={orgUrl("/legal/terms")}
                        className="underline hover:text-neutral-600"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                    privacy: (
                      <a
                        href={orgUrl("/legal/privacy")}
                        className="underline hover:text-neutral-600"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                  }}
                />
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-brand-primary"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.37.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}
